import {
  validateCardCaptureOptions,
  type CardCaptureOptions,
} from "@card-capture-lib/app/model/config.model";

function snakeToCamel(key: string): string {
  return key.replace(/_([a-zA-Z0-9])/g, (_, char: string) => char.toUpperCase());
}

// Recursively converts an JSON value's object keys from snake_case to camelCase
export function camelCaseKeysDeep<T>(value: unknown): T {
  if (Array.isArray(value)) {
    return value.map((item) => camelCaseKeysDeep(item)) as T;
  }

  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        snakeToCamel(key),
        camelCaseKeysDeep(val),
      ]),
    ) as T;
  }

  return value as T;
}

export function mapExchangeOptionsToCardCaptureShape(
  rawOptions: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    language: rawOptions.locale,
    cardTypes: rawOptions.card_types,
    theme: rawOptions.theme ? camelCaseKeysDeep(rawOptions.theme) : undefined,
  };
}

function withResolvedSubmitButtonText(
  theme: CardCaptureOptions["theme"],
  formattedAmount: string,
): CardCaptureOptions["theme"] {
  return {
    ...theme,
    text: {
      ...theme?.text,
      submitButton: theme?.text?.submitButton ?? `Pay ${formattedAmount}`,
    },
  };
}

export function assembleCardCaptureOptions(
  rawOptions: Record<string, unknown>,
  authenticationId: string | undefined,
  formattedAmount: string,
  disableSavingCards = false,
): CardCaptureOptions {
  const mappedOptions = mapExchangeOptionsToCardCaptureShape(rawOptions);
  const { language, cardTypes, theme } = validateCardCaptureOptions(mappedOptions);

  return {
    language,
    cardTypes,
    authenticationId,
    disableSavingCards,
    theme: withResolvedSubmitButtonText(theme, formattedAmount),
    // Checkout Page always collects an optional email address on the card capture step —
    // not exposed as a merchant-configurable option, unlike the rest of CardCaptureOptions.
    cardholderDetailFields: "EMAIL",
  };
}
